import React, { useState, useEffect, useMemo } from 'react';
import { FiSearch, FiX, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { getEventsByGuilds } from './eventService';
import { useAuth } from '../auth/AuthContext';
import { getCoordinatorById } from '../coordinators/coordinatorService';
import { getAllGuilds } from '../guilds/guildService';
import { EVENT_TYPES } from '../shared/constants';
import '../doctors/DoctorList.css';

export default function CoordinatorEventList() {
    const { currentUser } = useAuth();
    const [events, setEvents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [guilds, setGuilds] = useState([]);
    const [assignedGuilds, setAssignedGuilds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const filteredEvents = useMemo(() => {
        let filtered = [...events];

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(e =>
                (e.title?.toLowerCase().includes(searchLower)) ||
                (e.description?.toLowerCase().includes(searchLower))
            );
        }

        if (filterType) {
            filtered = filtered.filter(e => e.type === filterType);
        }

        return filtered;
    }, [events, searchTerm, filterType]);

    useEffect(() => {
        loadData();
    }, [currentUser]);

    async function loadData() {
        try {
            setLoading(true);
            const coordinator = await getCoordinatorById(currentUser.uid);
            const guildsData = await getAllGuilds();
            setGuilds(guildsData);
            setAssignedGuilds(coordinator?.assignedGuilds || []);

            const eventsData = await getEventsByGuilds(coordinator?.assignedGuilds || []);
            setEvents(eventsData);
            setError('');
        } catch (err) {
            setError('Failed to load events');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }


    function formatDate(timestamp) {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    function getGuildNames(targetGuildIds) {
        if (!targetGuildIds || targetGuildIds.length === 0) return 'None';
        if (targetGuildIds.includes('ALL')) return 'All Guilds';
        return targetGuildIds
            .map(id => guilds.find(g => g.id === id)?.name || id)
            .join(', ');
    }

    if (loading) {
        return (
            <div className="page-container">
                <div className="loading-spinner">
                    <FiRefreshCw className="spin-icon" />
                    <p>Loading events...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <h1>Events</h1>
                    <p className="subtitle">View events for your assigned guilds</p>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <FiX />
                    {error}
                </div>
            )}

            <div className="filters-section">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="filter-select"
                >
                    <option value="">All Types</option>
                    {EVENT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>

                {(searchTerm || filterType) && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterType('');
                        }}
                        className="btn-clear-filters"
                    >
                        <FiX />
                        <span>Clear Filters</span>
                    </button>
                )}
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Title & Description</th>
                            <th>Type</th>
                            <th>Date & Time</th>
                            <th>Mode</th>
                            <th>Target Guilds</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEvents.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="empty-state">
                                    <div className="empty-content">
                                        <FiCalendar className="empty-icon" />
                                        <p>
                                            {searchTerm || filterType
                                                ? 'No events match your filters.'
                                                : 'No events found for your assigned guilds.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredEvents.map((event) => (
                                <tr key={event.id}>
                                    <td className="name-cell">
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <strong>{event.title || 'Untitled'}</strong>
                                            {event.description && (
                                                <small style={{ color: '#64748b', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {event.description}
                                                </small>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge ${event.type === 'CME' ? 'badge-info' : 'badge-secondary'}`}>
                                            {event.type}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <FiCalendar size={14} style={{ color: 'var(--theme-primary)' }} />
                                                <span>{formatDate(event.date)}</span>
                                            </div>
                                            <small style={{ color: '#64748b', marginLeft: '20px' }}>
                                                {event.startTime} - {event.endTime}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ fontSize: '13px', fontWeight: 500 }}>{event.mode}</span>
                                    </td>
                                    <td className="guilds-cell">
                                        <div style={{ fontSize: '13px' }}>
                                            {getGuildNames(event.targetGuildIds)}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge ${event.status === 'Published' ? 'badge-success' : 'badge-warning'}`}>
                                            {event.status}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
